/* Shopify renders prices and line items. Native links/forms remain the fallback. */
(() => {
  class StorePanel extends HTMLElement {
    connectDialog() {
      this.events?.abort();
      this.events = new AbortController();
      this.dialog = this.querySelector('dialog');
      this.dialog.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !event.isComposing && !this.composing) { event.preventDefault(); event.stopPropagation(); this.dialog.close(); }
        if (event.key === 'Tab') {
          const targets = [...this.dialog.querySelectorAll('a[href],button,input,select,textarea,summary,[tabindex]')].filter(target => !target.disabled && target.tabIndex >= 0 && target.getClientRects().length);
          const first = targets[0], last = targets[targets.length - 1];
          if (first && (event.shiftKey ? document.activeElement === first : document.activeElement === last)) { event.preventDefault(); (event.shiftKey ? last : first).focus(); }
        }
      }, {signal: this.events.signal});
      this.dialog.addEventListener('close', () => {
        requestAnimationFrame(() => {
          if (!document.querySelector('dialog[open]')) {
            const target = this.trigger?.isConnected && this.trigger.getClientRects().length ? this.trigger : document.querySelector('[data-search-open]');
            target?.focus({preventScroll: true});
          }
        });
      }, {signal: this.events.signal});
    }
    show(trigger) {
      this.trigger = trigger || document.activeElement;
      document.querySelectorAll('dialog[open]').forEach(dialog => { if (dialog !== this.dialog) dialog.close(); });
      document.querySelectorAll('.site-header details[open]').forEach(menu => { menu.open = false; });
      if (!this.dialog.open) this.dialog.showModal();
    }
    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      clearTimeout(this.timer);
      if (this.dialog?.open) this.dialog.close();
    }
  }

  class SearchPanel extends StorePanel {
    connectedCallback() {
      this.connectDialog();
      this.input = this.querySelector('[name=q]');
      this.results = this.querySelector('[data-search-results]');
      this.initial = this.querySelector('[data-search-initial]');
      this.status = this.querySelector('[data-search-status]');
      this.clear = this.querySelector('[data-search-clear]');
      this.cache = new Map();
      this.usePredictive = this.dataset.predictive !== 'false';
      const options = {signal: this.events.signal};
      this.input.addEventListener('input', event => { if (!event.isComposing && !this.composing) this.schedule(); }, options);
      this.input.addEventListener('compositionstart', () => { this.composing = true; this.cancelSearch(); }, options);
      this.input.addEventListener('compositionend', () => { this.composing = false; this.schedule(); }, options);
      this.clear.addEventListener('click', () => { this.input.value = ''; this.schedule(); this.input.focus(); }, options);
      this.dialog.addEventListener('close', () => this.cancelSearch(), options);
      this.dialog.addEventListener('keydown', event => {
        const links = [...this.results.querySelectorAll('a')].filter(link => link.getClientRects().length);
        if (!links.length) return;
        const index = links.indexOf(document.activeElement);
        if (event.key === 'ArrowDown' && (event.target === this.input || index >= 0)) { event.preventDefault(); links[Math.min(index + 1, links.length - 1)].focus(); }
        if (event.key === 'ArrowUp' && index >= 0) { event.preventDefault(); (index ? links[index - 1] : this.input).focus(); }
      }, options);
    }
    open(trigger) { this.show(trigger); this.input.focus({preventScroll: true}); if (this.input.value.trim()) this.schedule(); }
    cancelSearch() { clearTimeout(this.timer); this.request?.abort(); this.request = null; this.results?.removeAttribute('aria-busy'); }
    schedule() {
      this.cancelSearch();
      const term = this.input.value.trim();
      this.clear.hidden = !term;
      this.results.replaceChildren();
      this.results.hidden = !term;
      this.initial.hidden = !!term;
      this.status.textContent = term ? this.dataset.loading : '';
      if (term) this.timer = setTimeout(() => this.search(term), 220);
    }
    async search(term) {
      if (this.cache.has(term)) { this.display(this.cache.get(term)); return; }
      const controller = new AbortController();
      this.request = controller;
      const timeout = setTimeout(() => controller.abort(), 10000);
      this.results.setAttribute('aria-busy', 'true');
      try {
        const url = this.searchURL(term);
        let response = await fetch(url, {signal: controller.signal, headers: {'Accept': 'text/html'}});
        if (response.status === 417 && this.usePredictive) {
          this.usePredictive = false;
          response = await fetch(this.searchURL(term), {signal: controller.signal, headers: {'Accept': 'text/html'}});
        }
        if (!response.ok) throw new Error('search');
        const markup = await response.text();
        if (this.request !== controller || this.input.value.trim() !== term || !this.dialog.open) return;
        this.display(markup);
        if (this.cache.size >= 12) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(term, markup);
      } catch {
        if (this.request === controller && this.input.value.trim() === term && this.dialog.open) this.status.textContent = this.dataset.error;
      } finally {
        clearTimeout(timeout);
        if (this.request === controller) { this.results.removeAttribute('aria-busy'); this.request = null; }
      }
    }
    searchURL(term) {
      const url = new URL(this.usePredictive ? this.dataset.suggestUrl : this.dataset.searchUrl, location.origin);
      url.searchParams.set('q', term); url.searchParams.set('section_id', 'predictive-search');
      if (this.usePredictive) {
        url.searchParams.set('resources[type]', 'product,collection,page,article');
        url.searchParams.set('resources[limit]', '6'); url.searchParams.set('resources[limit_scope]', 'all');
      } else { url.searchParams.set('type', 'product,article,page'); url.searchParams.set('options[prefix]', 'last'); }
      return url;
    }
    display(markup) {
      const content = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-search-content]');
      if (!content) throw new Error('search section');
      this.results.replaceChildren(content);
      this.status.textContent = content.dataset.resultLabel;
      this.results.hidden = false;
    }
  }

  class CartPanel extends StorePanel {
    connectedCallback() {
      this.connectDialog();
      this.content = this.querySelector('[data-cart-preview]');
      this.error = this.querySelector('[data-cart-error]');
      this.errorMessage = this.querySelector('[data-cart-error-message]');
      this.loading = this.querySelector('[data-cart-loading]');
      const options = {signal: this.events.signal};
      this.addEventListener('click', event => {
        const control = event.target.closest('[data-cart-step],[data-cart-remove]');
        if (!control || this.busy) return;
        const line = control.closest('[data-line-key]');
        const input = line.querySelector('[data-cart-quantity]');
        const min = Number(input.min) || 1;
        const step = Number(input.step) || 1;
        const current = Number(input.value);
        const quantity = control.hasAttribute('data-cart-remove') ? 0 : (Number(control.dataset.cartStep) < 0 && current <= min ? 0 : Math.max(min, current + Number(control.dataset.cartStep) * step));
        const max = input.max ? Number(input.max) : Infinity;
        if (quantity > max) return;
        this.change(line.dataset.lineKey, quantity, control.hasAttribute('data-cart-remove') ? '[data-cart-remove]' : `[data-cart-step="${control.dataset.cartStep}"]`);
      }, options);
      this.addEventListener('change', event => {
        const input = event.target.closest('[data-cart-quantity]');
        if (!input || this.busy || !input.reportValidity()) return;
        this.change(input.closest('[data-line-key]').dataset.lineKey, Number(input.value), '[data-cart-quantity]');
      }, options);
      this.addEventListener('submit', event => { if (this.busy) event.preventDefault(); }, options);
    }
    async open(trigger) { this.show(trigger); await this.load(); }
    setBusy(busy) {
      this.busy = busy;
      if (busy) this.content.setAttribute('aria-busy', 'true');
      else this.content.removeAttribute('aria-busy');
      this.content.querySelectorAll('button,input').forEach(control => {
        if (busy) { control.dataset.panelDisabled = String(control.disabled); control.disabled = true; }
        else if (control.hasAttribute('data-panel-disabled')) { control.disabled = control.dataset.panelDisabled === 'true'; delete control.dataset.panelDisabled; }
      });
    }
    showError(message) { this.errorMessage.textContent = message || this.dataset.error; this.error.hidden = false; }
    render(markup, {open = false, trigger = null, focusKey = null, focusSelector = null} = {}) {
      const content = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-cart-preview-content]');
      if (!content) return false;
      this.content.replaceChildren(content);
      this.error.hidden = true;
      this.loading.hidden = true;
      document.querySelectorAll('[data-cart-count]').forEach(counter => { counter.textContent = content.dataset.cartCountValue; counter.hidden = Number(content.dataset.cartCountValue) === 0; });
      if (open) this.show(trigger);
      if (focusKey && this.dialog.open) {
        const row = this.querySelector(`[data-line-key="${CSS.escape(focusKey)}"]`);
        const candidate = row?.querySelector(focusSelector);
        const target = candidate && !candidate.disabled ? candidate : this.querySelector('.drawer-item h3 a') || this.querySelector('.drawer-empty .button') || this.querySelector('[data-close-dialog]');
        target?.focus({preventScroll: true});
      }
      return true;
    }
    async load() {
      if (this.busy) return;
      this.setBusy(true); this.error.hidden = true; this.loading.hidden = false;
      const controller = new AbortController(); this.request = controller;
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const url = new URL(this.dataset.cartUrl, location.origin); url.searchParams.set('section_id', 'cart-preview');
        const response = await fetch(url, {signal: controller.signal, headers: {'Accept': 'text/html'}, cache: 'no-store'});
        if (!response.ok || !this.render(await response.text())) throw new Error('cart section');
      } catch { this.showError(); }
      finally { clearTimeout(timeout); this.request = null; this.loading.hidden = true; this.setBusy(false); }
    }
    async change(key, quantity, focusSelector) {
      if (this.busy) return;
      this.setBusy(true); this.error.hidden = true;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(`${this.dataset.changeUrl}.js`, {method: 'POST', signal: controller.signal, headers: {'Accept': 'application/json', 'Content-Type': 'application/json'}, body: JSON.stringify({id: key, quantity, sections: ['cart-preview'], sections_url: location.pathname})});
        const result = await response.json();
        if (!response.ok || result.status) {
          this.querySelectorAll('[data-cart-quantity]').forEach(input => { input.value = input.defaultValue; });
          this.showError(typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : null);
          return;
        }
        if (!result.sections?.['cart-preview'] || !this.render(result.sections['cart-preview'], {focusKey: key, focusSelector})) {
          // A successful change must never be posted twice because section rendering failed.
          location.assign(this.dataset.cartUrl); return;
        }
        document.getElementById('SiteStatus').textContent = this.dataset.updated;
        this.dispatchEvent(new CustomEvent('cart:updated', {bubbles: true}));
      } catch { this.showError(); }
      finally { clearTimeout(timeout); this.setBusy(false); }
    }
  }

  if (!customElements.get('search-panel')) customElements.define('search-panel', SearchPanel);
  if (!customElements.get('cart-panel')) customElements.define('cart-panel', CartPanel);
  document.addEventListener('click', event => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const trigger = event.target.closest('[data-search-open],[data-cart-open]');
    if (!trigger) return;
    const panel = document.querySelector(trigger.hasAttribute('data-search-open') ? 'search-panel' : 'cart-panel');
    if (!panel?.open || !panel.dialog?.showModal) return;
    event.preventDefault(); panel.open(trigger);
  });
})();
